import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { auth0Audience, Auth0Metadata } from '../../api/API';
import { CollegeAddress, ContactType, TranscriptRequest } from '../../api/models/TranscriptRequest';
import { Environment } from '../../Environment';
import { RootState } from '../../model/store';
import { studentSlice } from '../../model/studentSlice';
import { DateConverters } from '../../util/DateConverters';
import { regexValidators } from '../../util/RegexValidators';
import { Auth0Context, useAuth0 } from '../Auth0Provider';
import { ButtonState } from '../MNOHSButton';

/** Describes the shape of data needed for the transcript request form. */
export interface TransriptRequestFormState extends TranscriptRequest {}

export type TranscriptRequestScreenProps = {
  // inputs
  /** State of the transcript request form. */
  formState: TransriptRequestFormState;
  /** State of activity applied to the `Submit` button. */
  buttonState: ButtonState;
  /** Error message to be displayed, if one should be present. */
  error: string;
  /** Whether or not to show the success screen */
  showSuccessScreen: boolean;
  /** If still fetching student profile */
  isGettingProfile: boolean;

  // outputs
  /** Call when the user presses the submit button. */
  userPressedSubmit: () => void;
  /** Call when the user updates any text input on the transcript request screen. */
  userUpdatedTextInput: (target: string, value: string | CollegeAddress) => void;
  /** Call when the user presses any of the "radio" buttons. */
  userPresedRadioButton: (contactType: ContactType) => void;
};

export const transcriptRequestContainer = (
  TranscriptRequestScreen: React.ComponentType<TranscriptRequestScreenProps>
) => () => {
  const user = useSelector((state: RootState) => state.student.profile);
  const dispatch = useDispatch();

  const { api } = Environment.current();
  const { isAuthenticated, getTokenSilently, user: userInfo } = useAuth0() as Auth0Context;
  const isMounted = useRef(true);
  const converter = DateConverters['YYYY-MM-DD'];

  const [isGettingProfile, setIsGettingProfile] = useState(false);
  const [token, setToken] = useState('');
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [buttonState, setButtonState] = useState<ButtonState>('enabled');
  const [error, setError] = useState('');
  const [formState, setFormState] = useState<TransriptRequestFormState>({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phoneNumber: '',
    recipientSchoolName: '',
    contactType: ContactType.MAIL,
    applicationDeadline: converter.toString(new Date()),
    collegeAddress: { street: '', city: '', state: '', zip: '' },
    collegeFax: '',
    collegeEmail: '',
  });

  // To prevent updates on unmount
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    (async () => {
      if (isAuthenticated) {
        try {
          setIsGettingProfile(true);
          setButtonState('showingActivityIndicator');
          const personId = userInfo?.[Auth0Metadata.PersonId];
          const silentToken = await getTokenSilently({ audience: auth0Audience });
          const student = await api.getStudentById(personId, silentToken || '');
          if (isMounted.current) {
            setToken(silentToken || '');
            setIsGettingProfile(false);
            setButtonState('enabled');
            dispatch(studentSlice.actions.receivedStudent({ student }));
          }
        } catch (e) {
          toast.error(`${e.message} (Transcript Request)`);
          setIsGettingProfile(false);
          setButtonState('enabled');
        }
      }
    })();
  }, [isAuthenticated]);

  // listen to changes in user object
  React.useEffect(() => {
    if (isMounted.current) {
      setFormState({
        ...formState,
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
      });
    }
  }, [JSON.stringify(user)]);

  const collegeAddressIsValidated =
    formState.collegeAddress.city &&
    formState.collegeAddress.state &&
    formState.collegeAddress.street &&
    formState.collegeAddress.zip &&
    formState.collegeAddress.zip.match(regexValidators.zipCode);

  const collegeFaxIsValidated = formState.collegeFax && formState.collegeFax.match(regexValidators.fax);
  const collegeEmailIsValidated = formState.collegeEmail && formState.collegeEmail.match(regexValidators.email);

  const allowFormSubmission =
    formState.firstName &&
    formState.lastName &&
    formState.email &&
    formState.phoneNumber &&
    formState.phoneNumber.match(regexValidators.phoneNumber) &&
    formState.recipientSchoolName &&
    (collegeFaxIsValidated || collegeEmailIsValidated || collegeAddressIsValidated) &&
    formState.applicationDeadline;

  /** Renders a given error message for 5 seconds, then clears the error. */
  const _displayError = (message: string) => {
    setError(message);
    setTimeout(() => {
      setError('');
    }, 5000);
  };

  return (
    <TranscriptRequestScreen
      showSuccessScreen={showSuccessScreen}
      isGettingProfile={isGettingProfile}
      buttonState={allowFormSubmission ? buttonState : 'disabled'}
      formState={formState}
      error={error}
      userPressedSubmit={async () => {
        try {
          // 1.) Check that the form state is valid.
          if (collegeAddressIsValidated && !formState.collegeAddress.zip.match(regexValidators.zipCode)) {
            // 1a.) If the zipcode is invalid, show an error message.
            throw new Error('Please enter a valid zipcode and try again.');
          }
          if (!formState.phoneNumber.match(regexValidators.phoneNumber)) {
            // 1a.) If the phone number is invalid, show an error message.
            throw new Error('Please enter a valid US phone number and try again.');
          }
          if (formState.collegeFax && !formState.collegeFax.match(regexValidators.fax)) {
            // 1a.) If the fax number is invalid, show an error message.
            throw new Error('Please enter a valid fax number and try again.');
          }
          if (formState.collegeEmail && !formState.collegeEmail.match(regexValidators.email)) {
            // 1a.) If the email address is invalid, show an error message.
            throw new Error('Please enter a valid email address and try again.');
          }

          // 2.) If the form state is valid, show the activity indicator.
          setButtonState('showingActivityIndicator');

          // 3.) Make API call.
          if (!user?.id) throw new Error('No user ID found');
          const applicationDeadline = converter.toDate(formState.applicationDeadline)?.toJSON() || '';
          const result = await api.studentRequestTranscript(
            user.id,
            { ...formState, applicationDeadline },
            token || ''
          );

          // 4.) If successful, navigate to the confirmation screen.
          setShowSuccessScreen(true);
          setButtonState('enabled');
        } catch (e) {
          _displayError(e.message);
          setButtonState('enabled');
          return;
        }
      }}
      userUpdatedTextInput={(target: string, value: string | CollegeAddress) => {
        setFormState({ ...formState, [target]: value });
      }}
      userPresedRadioButton={(contactType: ContactType) => {
        switch (contactType) {
          case ContactType.MAIL:
            setFormState({ ...formState, contactType, collegeFax: '', collegeEmail: '' });
            break;
          case ContactType.EMAIL:
            setFormState({
              ...formState,
              contactType,
              collegeFax: '',
              collegeAddress: { city: '', state: '', street: '', zip: '' },
            });
            break;
          case ContactType.FAX:
            setFormState({
              ...formState,
              contactType,
              collegeEmail: '',
              collegeAddress: { city: '', state: '', street: '', zip: '' },
            });
            break;
          default:
            break;
        }
      }}
    />
  );
};
